import { Handle, type Node, type NodeProps, Position } from "@xyflow/react"
import type { ReactNode } from "react"
import { Activity, Bolt, Cog, Database, FileInput, GitCommitHorizontal, Lock, Scale, Shapes, Workflow } from "lucide-react"
import type { DeclaredMap, SourceDataset, TypeNode } from "@/lib/explorer"
import { useT } from "@/lib/i18n"
import { type WhyNode, type WhyNodeGroup, nodeGroup } from "@/lib/why"
import { cn } from "@/lib/utils"

export const NODE_WIDTH = 248
export const HEADER_HEIGHT = 44
export const ROW_HEIGHT = 24
export const MAX_ROWS = 8

export function typeNodeHeight(node: TypeNode): number {
  const rows = Math.min(node.properties.length, MAX_ROWS) + (node.properties.length > MAX_ROWS ? 1 : 0)
  return HEADER_HEIGHT + Math.max(rows, 1) * ROW_HEIGHT + 8
}

/** `commerce.SalesOrder` → `["commerce", "SalesOrder"]`. */
export function splitName(name: string): [string, string] {
  const i = name.lastIndexOf(".")
  return i === -1 ? ["", name] : [name.slice(0, i), name.slice(i + 1)]
}

function subtitle(parts: readonly (string | false | undefined)[]): string {
  return parts.filter(Boolean).join(" · ")
}

const hidden = "!opacity-0"

function Shell({ selected, dashed, muted, children }: { selected: boolean; dashed?: boolean; muted?: boolean; children: ReactNode }) {
  return (
    <div
      style={{ width: NODE_WIDTH }}
      className={cn(
        "rounded-lg border bg-card text-start shadow-[0_1px_2px_oklch(0_0_0/0.3)] transition-[border-color,box-shadow] duration-150",
        dashed ? "border-dashed border-subtle-foreground/60" : "border-border",
        muted && "bg-sidebar",
        selected && "border-primary shadow-[0_0_0_3px_var(--primary-soft)]",
      )}
    >
      {children}
    </div>
  )
}

export type TypeFlowNode = Node<{ node: TypeNode }, "type">

export function TypeNodeView({ data, selected }: NodeProps<TypeFlowNode>) {
  const t = useT()
  const { node } = data
  const [ns, local] = splitName(node.id)
  const shown = node.properties.slice(0, MAX_ROWS)
  return (
    <Shell selected={selected} dashed={node.form === "interface"} muted={node.layer === "registry"}>
      <Handle type="target" position={Position.Left} className={hidden} />
      <Handle type="source" position={Position.Right} className={hidden} />
      <div className="flex items-center gap-2 border-b border-border-subtle px-3" style={{ height: HEADER_HEIGHT }}>
        <Shapes className={cn("size-3.5 shrink-0", node.form === "interface" ? "text-subtle-foreground" : "text-primary")} aria-hidden />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-sm font-medium text-foreground">{local}</div>
          <div className="truncate text-2xs text-subtle-foreground">{subtitle([ns, node.form === "interface" && t("graph.interface"), node.extendedBy.length > 0 && `+ ${node.extendedBy.join(", ")}`])}</div>
        </div>
        {node.layer === "registry" ? <Lock className="size-3 shrink-0 text-subtle-foreground" aria-label={t("overview.registryBase")} /> : null}
      </div>
      <ul className="py-1">
        {shown.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-2 px-3" style={{ height: ROW_HEIGHT }} title={p.extension ? t("graph.addedByExtension", { name: p.extension }) : undefined}>
            <code className={cn("truncate font-mono text-2xs", p.extension ? "text-primary/90" : "text-foreground/85")}>
              {p.extension ? "+ " : ""}
              {p.id}
            </code>
            <span className="truncate text-2xs text-subtle-foreground">{p.type}</span>
          </li>
        ))}
        {node.properties.length > MAX_ROWS ? (
          <li className="px-3 text-2xs text-subtle-foreground" style={{ height: ROW_HEIGHT, lineHeight: `${ROW_HEIGHT}px` }}>
            {t("graph.moreProperties", { count: node.properties.length - MAX_ROWS })}
          </li>
        ) : null}
        {node.properties.length === 0 ? (
          <li className="px-3 text-2xs text-subtle-foreground" style={{ height: ROW_HEIGHT, lineHeight: `${ROW_HEIGHT}px` }}>
            {t("graph.noOwnProperties")}
          </li>
        ) : null}
      </ul>
    </Shell>
  )
}

function RowHeader({ icon, title, subtitle }: { icon: ReactNode; title: string; subtitle: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-border-subtle px-3 [&_svg]:size-3.5 [&_svg]:shrink-0" style={{ height: HEADER_HEIGHT }}>
      {icon}
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate text-sm font-medium text-foreground">{title}</div>
        <div className="truncate text-2xs text-subtle-foreground">{subtitle}</div>
      </div>
    </div>
  )
}

export type DatasetFlowNode = Node<{ dataset: SourceDataset; name: string }, "dataset">

export function DatasetNodeView({ data, selected }: NodeProps<DatasetFlowNode>) {
  const t = useT()
  return (
    <Shell selected={selected}>
      <RowHeader icon={<Database className="text-subtle-foreground" />} title={data.name} subtitle={t("graph.sourceDataset", { system: data.dataset.systemFallback ? t("graph.source") : data.dataset.system })} />
      <ul className="py-1">
        {data.dataset.fields.map((f) => (
          <li key={f.name} className="relative flex items-center justify-between gap-2 px-3" style={{ height: ROW_HEIGHT }}>
            <code className="truncate font-mono text-2xs text-foreground/85">{f.name}</code>
            <span className="truncate text-2xs text-subtle-foreground">{f.nativeType}</span>
            <Handle type="source" position={Position.Right} id={`f:${f.name}`} />
          </li>
        ))}
      </ul>
    </Shell>
  )
}

export type MapFlowNode = Node<{ map: DeclaredMap }, "map">

export function MapNodeView({ data, selected }: NodeProps<MapFlowNode>) {
  return (
    <Shell selected={selected} dashed>
      <RowHeader icon={<Workflow className="text-subtle-foreground" />} title="MappingIR" subtitle={data.map.mappingArtifactId} />
      <ul className="py-1">
        {data.map.rows.map((r) => (
          <li key={`${r.property}-${r.lifecycle}`} className="relative flex items-center gap-2 px-3" style={{ height: ROW_HEIGHT }} title={r.expression}>
            <Handle type="target" position={Position.Left} id={`in:${r.property}`} />
            <code className="min-w-0 flex-1 truncate font-mono text-2xs text-subtle-foreground">{r.expression || "—"}</code>
            <Handle type="source" position={Position.Right} id={`out:${r.property}`} />
          </li>
        ))}
      </ul>
    </Shell>
  )
}

export type TargetFlowNode = Node<{ type: string; properties: readonly string[]; mapped: ReadonlySet<string> }, "target">

export function TargetNodeView({ data, selected }: NodeProps<TargetFlowNode>) {
  const t = useT()
  const [ns, local] = splitName(data.type)
  return (
    <Shell selected={selected}>
      <RowHeader icon={<Shapes className="text-primary" />} title={local} subtitle={ns ? t("graph.namespacedObjectType", { namespace: ns }) : t("graph.objectType")} />
      <ul className="py-1">
        {data.properties.map((p) => (
          <li key={p} className="relative flex items-center justify-between gap-2 px-3" style={{ height: ROW_HEIGHT }}>
            <Handle type="target" position={Position.Left} id={`p:${p}`} />
            <code className={cn("truncate font-mono text-2xs", data.mapped.has(p) ? "text-foreground/85" : "text-subtle-foreground/70")}>{p}</code>
            {data.mapped.has(p) ? null : <span className="text-2xs text-subtle-foreground/70">{t("graph.unmapped")}</span>}
          </li>
        ))}
      </ul>
    </Shell>
  )
}

const WHY_ICON: Record<WhyNodeGroup, ReactNode> = {
  Fact: <Shapes />,
  Commit: <GitCommitHorizontal />,
  Decision: <Scale />,
  Observation: <FileInput />,
  Trigger: <Bolt />,
  SourceRecord: <Database />,
  Object: <Shapes />,
  Technical: <Cog />,
  Other: <Activity />,
}

export const WHY_NODE_WIDTH = 264
export const WHY_NODE_HEIGHT = 64

export type WhyFlowNode = Node<{ node: WhyNode; root: boolean }, "why">

export function WhyNodeView({ data, selected }: NodeProps<WhyFlowNode>) {
  const { node, root } = data
  const group = nodeGroup(node.kind)
  const kindLabel = group === "Other" || group === "Technical" ? node.kind : group
  const detail = node.objectType ? `${node.objectType}${node.key ? ` / ${node.key}` : ""}` : node.recordedAt
  const sub = detail ? `${kindLabel} · ${detail}` : kindLabel
  return (
    <div
      style={{ width: WHY_NODE_WIDTH, height: WHY_NODE_HEIGHT }}
      className={cn(
        "flex items-center gap-2.5 rounded-lg border bg-card px-3 shadow-[0_1px_2px_oklch(0_0_0/0.3)] transition-[border-color,box-shadow] duration-150",
        root ? "border-primary/70" : "border-border",
        selected && "border-primary shadow-[0_0_0_3px_var(--primary-soft)]",
      )}
    >
      <Handle type="target" position={Position.Top} className={hidden} />
      <Handle type="source" position={Position.Bottom} className={hidden} />
      <span className={cn("flex size-7 shrink-0 items-center justify-center rounded-md border [&_svg]:size-3.5", root ? "border-primary/40 bg-primary-soft text-primary" : "border-border-subtle bg-raised text-muted-foreground")}>{WHY_ICON[group]}</span>
      <div className="min-w-0 leading-tight">
        <div className="line-clamp-2 text-sm font-medium break-words text-foreground" title={node.label}>
          {node.label}
        </div>
        <div className="mt-0.5 truncate text-xs text-subtle-foreground" title={sub}>
          {sub}
        </div>
      </div>
    </div>
  )
}
